<template>
  <div class="acc" @mouseleave="hover = null">
    <svg
      class="acc-svg"
      :viewBox="`0 0 ${W} ${H}`"
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Evolución anual de calificación, NPS, CES, CSAT y volumen ESAC"
    >
      <!-- ---------- Rejilla horizontal ---------- -->
      <g class="acc-grid">
        <line
          v-for="(t, i) in ticksPct"
          :key="'g' + i"
          :x1="ML"
          :x2="W - MR"
          :y1="yPct(t)"
          :y2="yPct(t)"
        />
      </g>

      <!-- ---------- Eje izquierdo: calificación % ---------- -->
      <g class="acc-axis">
        <line :x1="ML" :x2="ML" :y1="MT" :y2="H - MB" />
        <text
          v-for="(t, i) in ticksPct"
          :key="'lt' + i"
          :x="ML - 8"
          :y="yPct(t) + 3.5"
          text-anchor="end"
        >
          {{ t.toFixed(0) }}
        </text>
        <text
          class="acc-axis-title"
          :transform="`rotate(-90 ${ML - 40} ${(MT + H - MB) / 2})`"
          :x="ML - 40"
          :y="(MT + H - MB) / 2"
          text-anchor="middle"
        >
          Calificación %
        </text>
      </g>

      <!-- ---------- Eje derecho: NPS ---------- -->
      <g class="acc-axis acc-axis-nps">
        <line :x1="W - MR" :x2="W - MR" :y1="MT" :y2="H - MB" />
        <text
          v-for="(t, i) in ticksNps"
          :key="'rt' + i"
          :x="W - MR + 8"
          :y="yNps(t) + 3.5"
          text-anchor="start"
        >
          {{ t.toFixed(0) }}
        </text>
        <text
          class="acc-axis-title"
          :transform="`rotate(90 ${W - MR + 40} ${(MT + H - MB) / 2})`"
          :x="W - MR + 40"
          :y="(MT + H - MB) / 2"
          text-anchor="middle"
        >
          NPS
        </text>
      </g>

      <!-- ---------- Barras de volumen (fondo) ---------- -->
      <g>
        <template v-for="(p, i) in serie" :key="'b' + p.anio">
          <rect
            class="acc-bar bar-encuestas"
            :x="cx(i) - 24"
            :y="yVol(p.encuestas)"
            :width="22"
            :height="Math.max(0, H - MB - yVol(p.encuestas))"
            rx="4"
          />
          <rect
            class="acc-bar bar-cuentas"
            :x="cx(i) + 4"
            :y="yVol(p.cuentas)"
            :width="18"
            :height="Math.max(0, H - MB - yVol(p.cuentas))"
            rx="4"
          />
        </template>
      </g>

      <!-- ---------- Líneas ---------- -->
      <g class="acc-lines" fill="none">
        <polyline
          v-for="(d, i) in segmentos('ces', yAux)"
          :key="'ces' + i"
          class="ln ln-ces"
          :points="d"
        />
        <polyline
          v-for="(d, i) in segmentos('csat', yAux)"
          :key="'csat' + i"
          class="ln ln-csat"
          :points="d"
        />
        <polyline
          v-for="(d, i) in segmentos('nps', yNps)"
          :key="'nps' + i"
          class="ln ln-nps"
          :points="d"
        />
        <!-- Referencia ONEST: solo cuando el tablero está acotado a una cuenta -->
        <polyline
          v-for="(d, i) in segmentosBenchmark"
          :key="'bm' + i"
          class="ln ln-bench"
          :points="d"
        />
        <polyline
          v-for="(d, i) in segmentos('score', yPct)"
          :key="'sc' + i"
          class="ln ln-score"
          :points="d"
        />
      </g>

      <!-- ---------- Puntos ---------- -->
      <g>
        <template v-for="(p, i) in serie" :key="'d' + p.anio">
          <circle
            v-if="p.ces != null"
            class="dot dot-ces"
            :cx="cx(i)"
            :cy="yAux(p.ces)"
            r="3.5"
          />
          <circle
            v-if="p.csat != null"
            class="dot dot-csat"
            :cx="cx(i)"
            :cy="yAux(p.csat)"
            r="3.5"
          />
          <circle
            v-if="p.nps != null"
            class="dot dot-nps"
            :cx="cx(i)"
            :cy="yNps(p.nps)"
            r="4"
          />
          <circle
            v-if="p.score != null"
            class="dot dot-score"
            :cx="cx(i)"
            :cy="yPct(p.score)"
            :r="hover === i ? 7 : 5"
          />
        </template>
      </g>

      <!-- ---------- Eje X y zonas de hover ---------- -->
      <g>
        <template v-for="(p, i) in serie" :key="'x' + p.anio">
          <text class="acc-xlabel" :x="cx(i)" :y="H - MB + 20" text-anchor="middle">
            {{ p.anio }}
          </text>
          <rect
            class="acc-hit"
            :x="ML + bandW * i"
            :y="MT"
            :width="bandW"
            :height="H - MB - MT"
            @mouseenter="hover = i"
          />
        </template>
      </g>
    </svg>

    <!-- ---------- Tooltip ---------- -->
    <div
      v-if="hoverPunto"
      class="acc-tip"
      :class="hover > serie.length / 2 ? 'tip-left' : 'tip-right'"
      :style="{ left: (cx(hover) / W) * 100 + '%' }"
    >
      <p class="tip-title">Año {{ hoverPunto.anio }}</p>
      <p>
        Calificación global: <strong>{{ nd(hoverPunto.score) }}%</strong>
        <span v-if="deltaScore != null" :class="deltaScore >= 0 ? 'up' : 'down'">
          ({{ deltaScore >= 0 ? "+" : "" }}{{ deltaScore }} vs año ant.)
        </span>
      </p>
      <p>
        NPS: <strong>{{ nd(hoverPunto.nps, 0) }}</strong>
        <span v-if="deltaNps != null" class="tip-dim">
          ({{ deltaNps >= 0 ? "+" : "" }}{{ deltaNps }})
        </span>
      </p>
      <p>
        CES: <strong>{{ nd(hoverPunto.ces) }}</strong> · CSAT:
        <strong>{{ nd(hoverPunto.csat) }}</strong>
        <span v-if="hoverPunto.csat == null" class="tip-dim">(sin dato)</span>
      </p>
      <p>
        Cuentas evaluadas: <strong>{{ hoverPunto.cuentas }}</strong> · Encuestas:
        <strong>{{ hoverPunto.encuestas }}</strong>
      </p>
      <p>
        Cobertura: <strong>{{ hoverPunto.cobertura }}%</strong>
      </p>
      <p class="tip-src">Fuente: Encuestas ESAC</p>
    </div>

    <!-- ---------- Leyenda ---------- -->
    <div class="acc-legend">
      <span><i class="lg lg-encuestas"></i> Encuestas aplicadas</span>
      <span><i class="lg lg-cuentas"></i> Cuentas evaluadas</span>
      <span>
        <i class="lg lg-score"></i>
        {{ benchmark ? "Calificación de la cuenta" : "Calificación global ONEST" }}
      </span>
      <span v-if="benchmark"><i class="lg lg-bench"></i> ONEST global (ref.)</span>
      <span><i class="lg lg-ces"></i> CES</span>
      <span><i class="lg lg-csat"></i> CSAT</span>
      <span><i class="lg lg-nps"></i> NPS</span>
    </div>

    <p class="acc-caption">
      Eje izquierdo: calificación % · Eje derecho: NPS · Líneas CES/CSAT en
      escala propia · Barras: volumen ESAC
    </p>
  </div>
</template>

<script>
// Escala lineal valor -> coordenada Y (invertida: el 0 del SVG está arriba).
function escala([min, max], yTop, yBottom) {
  const rango = max - min || 1;
  return (v) => yBottom - ((v - min) / rango) * (yBottom - yTop);
}

// Dominio con holgura. La referencia usa dominios fijos ([75,95] para score,
// [30,60] para NPS), pero con datos reales el NPS llega a 64 y quedaría
// recortado, así que se calculan a partir de los valores presentes.
function dominio(valores, holgura = 0.18) {
  const v = valores.filter((x) => x != null && !Number.isNaN(x));
  if (!v.length) return [0, 1];
  let min = Math.min(...v);
  let max = Math.max(...v);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const pad = (max - min) * holgura;
  return [min - pad, max + pad];
}

function ticks([min, max], n = 5) {
  const paso = (max - min) / (n - 1);
  return Array.from({ length: n }, (_, i) => min + paso * i);
}

export default {
  name: "AnnualComposedChart",

  props: {
    // [{ anio, score, nps, ces, csat, encuestas, cuentas, cobertura }]
    serie: { type: Array, default: () => [] },
    // Serie ONEST global para superponer como referencia punteada cuando el
    // tablero está acotado a una cuenta. null = vista global, no se dibuja.
    benchmark: { type: Array, default: null },
  },

  data() {
    return {
      hover: null,
      // Lienzo en unidades de viewBox; el SVG escala al ancho del contenedor.
      W: 820,
      H: 360,
      ML: 62,
      MR: 62,
      MT: 16,
      MB: 34,
    };
  },

  computed: {
    bandW() {
      return (this.W - this.ML - this.MR) / (this.serie.length || 1);
    },

    // El dominio de calificación incluye la referencia para que ninguna de las
    // dos líneas quede recortada.
    domPct() {
      return dominio([
        ...this.serie.map((p) => p.score),
        ...(this.benchmark || []).map((p) => p.score),
      ]);
    },

    segmentosBenchmark() {
      if (!this.benchmark || !this.benchmark.length) return [];
      const puntos = this.serie
        .map((p, i) => {
          const b = this.benchmark.find((x) => x.anio === p.anio);
          return b && b.score != null ? `${this.cx(i)},${this.yPct(b.score)}` : null;
        })
        .filter(Boolean);
      return puntos.length > 1 ? [puntos.join(" ")] : [];
    },
    domNps() {
      return dominio(this.serie.map((p) => p.nps));
    },
    // CES y CSAT comparten escala: son ambos 0-10 y se leen comparados.
    domAux() {
      return dominio([
        ...this.serie.map((p) => p.ces),
        ...this.serie.map((p) => p.csat),
      ]);
    },
    // Las barras arrancan siempre en 0 y ocupan la franja inferior.
    domVol() {
      const max = Math.max(
        1,
        ...this.serie.map((p) => Math.max(p.encuestas || 0, p.cuentas || 0)),
      );
      return [0, max * 1.15];
    },

    ticksPct() {
      return ticks(this.domPct);
    },
    ticksNps() {
      return ticks(this.domNps);
    },

    hoverPunto() {
      return this.hover == null ? null : this.serie[this.hover] || null;
    },

    previo() {
      return this.hover ? this.serie[this.hover - 1] : null;
    },

    deltaScore() {
      if (!this.hoverPunto || !this.previo) return null;
      if (this.hoverPunto.score == null || this.previo.score == null) return null;
      return Math.round((this.hoverPunto.score - this.previo.score) * 10) / 10;
    },

    deltaNps() {
      if (!this.hoverPunto || !this.previo) return null;
      if (this.hoverPunto.nps == null || this.previo.nps == null) return null;
      return this.hoverPunto.nps - this.previo.nps;
    },
  },

  methods: {
    cx(i) {
      return this.ML + this.bandW * (i + 0.5);
    },

    yPct(v) {
      return escala(this.domPct, this.MT, this.H - this.MB)(v);
    },
    yNps(v) {
      return escala(this.domNps, this.MT, this.H - this.MB)(v);
    },
    yAux(v) {
      return escala(this.domAux, this.MT, this.H - this.MB)(v);
    },
    yVol(v) {
      return escala(this.domVol, this.MT, this.H - this.MB)(v || 0);
    },

    // Divide la serie en tramos continuos: un valor nulo corta la línea en
    // lugar de arrastrarla a cero (p. ej. CSAT sin dato en 2024).
    segmentos(clave, escalaY) {
      const tramos = [];
      let actual = [];
      this.serie.forEach((p, i) => {
        const v = p[clave];
        if (v == null) {
          if (actual.length) tramos.push(actual);
          actual = [];
          return;
        }
        actual.push(`${this.cx(i)},${escalaY(v)}`);
      });
      if (actual.length) tramos.push(actual);
      return tramos.filter((t) => t.length > 1).map((t) => t.join(" "));
    },

    nd(valor, decimales = 1) {
      return valor == null ? "—" : Number(valor).toFixed(decimales);
    },
  },
};
</script>

<style scoped>
.acc {
  position: relative;
}

.acc-svg {
  display: block;
  overflow: visible;
}

/* ---------- Ejes y rejilla ---------- */
.acc-grid line {
  stroke: var(--border);
  stroke-dasharray: 3 3;
}

.acc-axis line {
  stroke: var(--border-2);
}

.acc-axis text {
  font-size: 11px;
  fill: var(--txt-dim);
  font-variant-numeric: tabular-nums;
}

.acc-axis-title {
  font-size: 10px;
  font-weight: 600;
  fill: var(--txt-faint);
}

.acc-axis-nps text {
  fill: var(--verde);
}

.acc-xlabel {
  font-size: 12px;
  font-weight: 700;
  fill: var(--heading);
}

.acc-hit {
  fill: transparent;
}

/* ---------- Barras ---------- */
.bar-encuestas {
  fill: #dce8ff;
}
.bar-cuentas {
  fill: #a8c4f0;
}
html.dark .bar-encuestas {
  fill: #24314d;
}
html.dark .bar-cuentas {
  fill: #33456b;
}

/* ---------- Líneas ---------- */
.ln {
  stroke-linecap: round;
  stroke-linejoin: round;
}
.ln-score {
  stroke: var(--primary);
  stroke-width: 3;
}
.ln-nps {
  stroke: var(--verde);
  stroke-width: 2.5;
}
.ln-bench {
  stroke: var(--txt-faint);
  stroke-width: 2;
  stroke-dasharray: 6 4;
}
.ln-ces {
  stroke: var(--primary-dark);
  stroke-width: 2;
}
.ln-csat {
  stroke: var(--amarillo);
  stroke-width: 2;
}
html.dark .ln-ces {
  stroke: #8fb8ff;
}

.dot {
  stroke: var(--white);
  stroke-width: 2;
}
.dot-score {
  fill: var(--primary);
}
.dot-nps {
  fill: var(--verde);
}
.dot-ces {
  fill: var(--primary-dark);
  stroke-width: 1.5;
}
.dot-csat {
  fill: var(--amarillo);
  stroke-width: 1.5;
}
html.dark .dot-ces {
  fill: #8fb8ff;
}

/* ---------- Tooltip ---------- */
.acc-tip {
  position: absolute;
  top: 10px;
  min-width: 232px;
  background: var(--white);
  border: 1px solid var(--border-2);
  border-radius: 12px;
  padding: 10px 12px;
  box-shadow: var(--shadow-md);
  font-size: 12px;
  color: var(--txt-dim);
  pointer-events: none;
  z-index: 5;
}
.tip-right {
  transform: translateX(14px);
}
.tip-left {
  transform: translateX(-100%) translateX(-14px);
}
.acc-tip p {
  margin: 0 0 3px;
  line-height: 1.45;
}
.acc-tip strong {
  color: var(--txt);
}
.tip-title {
  font-weight: 700;
  color: var(--txt);
  margin-bottom: 6px !important;
}
.acc-tip .up {
  color: var(--verde);
  font-weight: 600;
  font-size: 11px;
}
.acc-tip .down {
  color: var(--rojo);
  font-weight: 600;
  font-size: 11px;
}
.tip-dim {
  color: var(--txt-faint);
  font-size: 11px;
}
.tip-src {
  margin-top: 7px !important;
  margin-bottom: 0 !important;
  font-size: 10.5px;
  color: var(--txt-faint);
}

/* ---------- Leyenda y pie ---------- */
.acc-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px 18px;
  margin-top: 12px;
  font-size: 12px;
  color: var(--txt-dim);
}
.acc-legend .lg {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 5px;
}
.lg-encuestas {
  background: #dce8ff;
  outline: 1px solid var(--border-2);
}
.lg-cuentas {
  background: #a8c4f0;
}
.lg-score {
  background: var(--primary);
}
.lg-bench {
  background: var(--txt-faint);
}
.lg-ces {
  background: var(--primary-dark);
}
.lg-csat {
  background: var(--amarillo);
}
.lg-nps {
  background: var(--verde);
}
html.dark .lg-encuestas {
  background: #24314d;
}
html.dark .lg-cuentas {
  background: #33456b;
}
html.dark .lg-ces {
  background: #8fb8ff;
}

.acc-caption {
  margin: 8px 0 0;
  text-align: center;
  font-size: 10px;
  font-weight: 500;
  color: var(--txt-faint);
}
</style>
